import React, { useEffect, useState } from "react";
import { FileText, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { translateFr as tfr } from "@/i18n";

export const usePreviewUrl = (cid, doc) => {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!cid || !doc) { setUrl(null); return undefined; }
    let objectUrl = null;
    let cancelled = false;
    setLoading(true);
    api.get(`/contracts/${cid}/documents/${doc.id}/download`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(r.data);
        setUrl(objectUrl);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [cid, doc]);
  return { url, loading };
};

export const downloadDoc = async (cid, doc) => {
  const r = await api.get(`/contracts/${cid}/documents/${doc.id}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = doc.filename;
  a.click();
  URL.revokeObjectURL(url);
};

export const PdfPreview = ({ cid, doc, height = 520 }) => {
  const { url, loading } = usePreviewUrl(cid, doc);
  if (!doc) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-white/[0.09] text-muted-foreground" style={{ height }} data-testid="pdf-preview-empty">
        <FileText className="h-8 w-8 opacity-40" />
        <p className="text-sm">{tfr("Aucun document principal pour le moment")}</p>
      </div>
    );
  }
  if (loading || !url) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-white/[0.07] bg-black/20" style={{ height }} data-testid="pdf-preview-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  const isPdf = doc.mime === "application/pdf";
  const isImage = (doc.mime || "").startsWith("image/");
  if (isPdf) {
    return <iframe title={doc.filename} src={`${url}#toolbar=0`} className="w-full rounded-xl border border-white/[0.07] bg-white" style={{ height }} data-testid="pdf-preview-frame" />;
  }
  if (isImage) {
    return <img src={url} alt={doc.filename} className="w-full rounded-xl border border-white/[0.07] object-contain bg-black/20" style={{ maxHeight: height }} data-testid="pdf-preview-image" />;
  }
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-white/[0.07] bg-black/20" style={{ height }} data-testid="pdf-preview-fallback">
      <FileText className="h-8 w-8 opacity-50" />
      <p className="text-sm text-muted-foreground">Aperçu non disponible pour ce format ({doc.mime})</p>
      <Button size="sm" variant="outline" onClick={() => downloadDoc(cid, doc)}><Download className="h-4 w-4 mr-1.5" />{tfr("Télécharger")}</Button>
    </div>
  );
};
